import type { Resolver } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Button, DateInput, Select, TimeInput } from '@/components/ui'
import { useCreateTimeCorrectionRequest } from '@/features/attendance/hooks/useTimeCorrection'
import {
  timeCorrectionRequestFormSchema,
  buildTimeCorrectionRequestDto,
  REASON_CODES,
  type TimeCorrectionRequestFormData,
} from '@/features/attendance/schemas/timeCorrection.schema'
import { cn } from '@/lib/cn'

export interface TimeCorrectionRequestModalProps {
  companyId: string
  attendanceId: string
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const defaultValues: TimeCorrectionRequestFormData = {
  reason: '',
  requestedClockInDate: '',
  requestedClockInTime: '',
  requestedClockOutDate: '',
  requestedClockOutTime: '',
  reasonCode: '',
}

const CLOCK_FIELD_GRID =
  'grid grid-cols-1 gap-2.5 sm:grid-cols-[7.5rem_minmax(0,1fr)_9rem] sm:items-center'

export function TimeCorrectionRequestModal({
  companyId,
  attendanceId,
  open,
  onClose,
  onSuccess,
}: TimeCorrectionRequestModalProps): React.ReactElement {
  const createRequest = useCreateTimeCorrectionRequest()
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TimeCorrectionRequestFormData>({
    resolver: zodResolver(
      timeCorrectionRequestFormSchema
    ) as Resolver<TimeCorrectionRequestFormData>,
    defaultValues,
  })

  const onSubmit = (data: TimeCorrectionRequestFormData) => {
    const body = buildTimeCorrectionRequestDto(data, attendanceId)
    createRequest.mutate(
      { companyId, body },
      {
        onSuccess: () => {
          reset(defaultValues)
          onClose()
          onSuccess?.()
        },
      }
    )
  }

  if (!open) return <></>

  const reasonCodeOptions = [
    { value: '', label: 'Select reason (optional)' },
    ...REASON_CODES.map((r) => ({ value: r.value, label: r.label })),
  ]

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request time correction"
      className="max-w-xl"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="time-correction-request-form"
            loading={isSubmitting || createRequest.isPending}
          >
            Submit request
          </Button>
        </>
      }
    >
      <form
        id="time-correction-request-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5"
      >
        <div>
          <label
            htmlFor="time-correction-reason"
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            id="time-correction-reason"
            {...register('reason')}
            rows={3}
            className={cn(
              'block w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500',
              errors.reason
                ? 'border-danger focus:ring-danger'
                : 'border-gray-300 dark:border-gray-600'
            )}
            placeholder="e.g. Forgot to clock out"
            aria-invalid={!!errors.reason}
          />
          {errors.reason && <p className="mt-1 text-sm text-danger">{errors.reason.message}</p>}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason code (optional)
          </label>
          <Select
            options={reasonCodeOptions}
            value={watch('reasonCode') ?? ''}
            onChange={(v) => setValue('reasonCode', v ?? '', { shouldValidate: true })}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3.5 dark:border-gray-700 dark:bg-gray-900/30">
          <div className="mb-3 hidden gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:grid sm:grid-cols-[7.5rem_minmax(0,1fr)_9rem]">
            <span className="hidden sm:block" />
            <span>Date</span>
            <span>Time</span>
          </div>

          <div className="space-y-3">
            <div className={CLOCK_FIELD_GRID}>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Clock-in</span>
              <DateInput {...register('requestedClockInDate')} aria-label="Clock-in date" />
              <TimeInput
                value={watch('requestedClockInTime') ?? ''}
                onChange={(v) => setValue('requestedClockInTime', v, { shouldValidate: true })}
              />
            </div>

            <div className={CLOCK_FIELD_GRID}>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Clock-out
              </span>
              <DateInput {...register('requestedClockOutDate')} aria-label="Clock-out date" />
              <TimeInput
                value={watch('requestedClockOutTime') ?? ''}
                onChange={(v) => setValue('requestedClockOutTime', v, { shouldValidate: true })}
              />
            </div>
          </div>
        </div>

        {(errors.requestedClockInDate ?? errors.requestedClockOutDate) && (
          <p className="text-sm text-danger">
            {errors.requestedClockInDate?.message ?? errors.requestedClockOutDate?.message}
          </p>
        )}
      </form>
    </Modal>
  )
}

TimeCorrectionRequestModal.displayName = 'TimeCorrectionRequestModal'
